<template>
  <nav
    class="app-sidebar flex h-full flex-col border-r border-slate-200 bg-white shadow-soft"
  >
    <div
      :class="[
        'sidebar-header flex min-h-11 items-center gap-1.5 border-b border-slate-100 px-2.5 py-1.5',
        collapsed ? 'justify-center px-2' : 'justify-between',
      ]"
    >
      <router-link
        v-if="!collapsed"
        to="/home"
        :class="['flex items-center gap-2 overflow-hidden']"
        @click="$emit('navigate')"
      >
<!--        <img-->
<!--          src="../assets/logoMBF.png"-->
<!--          alt="MBF"-->
<!--          class="w-[70%] object-contain transition-all"-->
<!--        />-->
      </router-link>
      <button
        type="button"
        class="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-brand-50 hover:text-brand-700 lg:flex"
        :title="collapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'"
        @click="$emit('toggle-collapse')"
      >
        <component
          :is="collapsed ? PanelLeftOpen : PanelLeftClose"
          :size="16"
          :stroke-width="2.2"
        />
      </button>
    </div>

    <div
      :class="['sidebar-menu min-h-0 flex-1 overflow-y-auto p-1.5', collapsed ? 'px-1' : '']"
    >
      <p
        v-if="!collapsed"
        class="px-1.5 pb-1 text-[9px] font-bold uppercase tracking-wide text-slate-400"
      >
        Menu chức năng
      </p>

      <template v-for="entry in menuEntries" :key="entry.key">
        <router-link
          v-if="entry.type === 'item'"
          :to="entry.to"
          :title="entry.key === 'maintenance' && (maintenanceAlert.tongSo > 0 || maintenanceAlert.error)
            ? maintenanceAlertTitle
            : collapsed ? entry.label : ''"
          :class="[
            'sidebar-link relative flex min-h-8 items-center rounded-lg py-0.5 text-[11px] font-semibold text-slate-600 transition hover:bg-brand-50 hover:text-brand-700',
            collapsed ? 'justify-center px-0' : 'gap-1.5 px-1.5',
          ]"
          active-class="bg-brand-600 text-white hover:bg-brand-600 hover:text-white shadow-card"
          @click="$emit('navigate')"
        >
          <span
            class="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md bg-current/10"
          >
            <component :is="entry.icon" :size="15" :stroke-width="2.2" />
          </span>
          <span v-if="!collapsed" class="min-w-0 flex-1 truncate">{{ entry.label }}</span>
          <span
            v-if="entry.key === 'maintenance' && (maintenanceAlert.tongSo > 0 || maintenanceAlert.error)"
            :class="[
              'inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-md px-1 text-[10px] font-bold leading-none shadow-sm',
              maintenanceAlert.error
                ? 'bg-amber-100 text-amber-700'
                : 'bg-rose-500 text-white',
              collapsed ? 'absolute -right-0.5 -top-0.5' : 'relative ml-auto',
            ]"
            :aria-label="maintenanceAlertTitle"
          >
            <span class="relative">
              {{ maintenanceAlert.error ? "!" : maintenanceAlert.tongSo > 99 ? "99+" : maintenanceAlert.tongSo }}
            </span>
          </span>
        </router-link>

        <div v-else class="mb-0.5">
          <button
            v-if="!collapsed"
            type="button"
            class="sidebar-group-title flex min-h-7 w-full items-center justify-between gap-1.5 rounded-lg px-1.5 py-0.5 text-[10px] font-bold text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
            @click="toggleGroup(entry.key)"
          >
            <span class="truncate text-left">{{ entry.label }}</span>
            <ChevronDown
              :size="15"
              :stroke-width="2.3"
              :class="[
                'shrink-0 transition-transform',
                openGroups[entry.key] ? 'rotate-180' : '',
              ]"
            />
          </button>

          <div
            v-show="collapsed || openGroups[entry.key]"
            class="space-y-px"
          >
            <router-link
              v-for="item in entry.children"
              :key="item.to"
              :to="item.to"
              :title="collapsed ? item.label : ''"
              :class="[
                'sidebar-child-link flex min-h-8 items-center rounded-lg py-0.5 text-[11px] font-semibold text-slate-600 transition hover:bg-brand-50 hover:text-brand-700',
                collapsed ? 'justify-center px-0' : 'gap-1.5 px-1.5 pl-2.5',
              ]"
              active-class="bg-brand-600 text-white hover:bg-brand-600 hover:text-white shadow-card"
              @click="$emit('navigate')"
            >
              <span
                class="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md bg-current/10"
              >
                <component :is="item.icon" :size="15" :stroke-width="2.2" />
              </span>
              <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </div>

    <div :class="['sidebar-footer border-t border-slate-100 p-1.5', collapsed ? 'px-1' : '']">
      <div
        :class="[
          'mb-1 rounded-lg bg-slate-100 p-1.5 text-left',
          collapsed ? 'flex h-8 items-center justify-center p-0' : '',
        ]"
        :title="collapsed ? fullName || permissionLabel : ''"
      >
        <p v-if="collapsed" class="text-xs font-bold text-ink">
          {{ initials }}
        </p>
        <p v-if="!collapsed" class="truncate text-xs font-bold text-ink">
          {{ fullName || "Người dùng" }}
        </p>
        <p v-if="!collapsed" class="truncate text-[11px] text-slate-500">
          {{ team || permissionLabel }}
        </p>
      </div>
      <UiButton
        variant="secondary"
        block
        :size="collapsed ? 'icon' : 'md'"
        icon="logout"
        :title="collapsed ? 'Đăng xuất' : ''"
        @click="logout"
      >
        <span v-if="!collapsed">Đăng xuất</span>
      </UiButton>
    </div>
  </nav>
</template>

<script lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import router from "@/router";
import QLTSRepository from "@/services/QLTSRepository";
import {
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  ChevronDown,
  ClipboardCheck,
  ClipboardPlus,
  ClipboardList,
  FileText,
  Layers3,
  ListTree,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  UserPlus,
  Users,
} from "lucide-vue-next";

type MenuItem = {
  type: "item";
  key: string;
  label: string;
  icon: unknown;
  to: string;
};

type MenuGroup = {
  type: "group";
  key: string;
  label: string;
  children: Array<Omit<MenuItem, "type" | "key">>;
};

type MenuEntry = MenuItem | MenuGroup;

export default {
  components: { ChevronDown, LogOut },
  props: {
    collapsed: { type: Boolean, default: false },
    fullName: { type: String, default: "" },
    team: { type: String, default: "" },
  },
  emits: ["navigate", "toggle-collapse"],
  setup(props: any) {
    const store = useStore();
    const route = useRoute();
    const maintenanceAlert = reactive({
      tongSo: 0,
      quaHan: 0,
      sapDenHan: 0,
      soNgayCanhBao: 0,
      loaded: false,
      error: false,
    });
    let maintenanceAlertTimer: number | undefined;
    const deviceGroup: MenuGroup = {
      type: "group",
      key: "device-management",
      label: "Quản lý thiết bị",
      children: [
        {
          label: "Tiếp nhận thiết bị",
          icon: ClipboardPlus,
          to: "/inputDevice",
        },
        {
          label: "Tình trạng",
          icon: ClipboardCheck,
          to: "/managerDevicePage",
        },
        { label: "Tất cả thiết bị", icon: Boxes, to: "/allDevicePage" },
      ],
    };

    const categoryGroup: MenuGroup = {
      type: "group",
      key: "category-management",
      label: "Quản lý danh mục",
      children: [
        { label: "Khoa phòng", icon: Building2, to: "/departments" },
        { label: "Nhóm thiết bị", icon: Layers3, to: "/device-groups" },
        {
          label: "Chi tiết loại thiết bị",
          icon: ListTree,
          to: "/device-type-details",
        },
      ],
    };

    const maintenanceItem: MenuItem = {
      type: "item",
      key: "maintenance",
      label: "Bảo trì bảo dưỡng",
      icon: ClipboardList,
      to: "/maintenance-history",
    };

    const helpItem: MenuItem = {
      type: "item", key: "help", label: "Hướng dẫn sử dụng", icon: BookOpen, to: "/help",
    };

    const adminMenuEntries: MenuEntry[] = [
      {
        type: "item",
        key: "overview",
        label: "Tổng quan",
        icon: BarChart3,
        to: "/chartPage",
      },
      deviceGroup,
      categoryGroup,
      maintenanceItem,
      {
        type: "item",
        key: "reports",
        label: "Báo cáo",
        icon: FileText,
        to: "/reportPage",
      },
      {
        type: "item",
        key: "users",
        label: "Tài khoản",
        icon: Users,
        to: "/userlist",
      },
      {
        type: "item",
        key: "register",
        label: "Đăng ký tài khoản",
        icon: UserPlus,
        to: "/registerpage",
      },
      helpItem,
    ];

    const userMenuEntries: MenuEntry[] = [
      {
        type: "item",
        key: "overview",
        label: "Tổng quan",
        icon: BarChart3,
        to: "/chartPage",
      },
      {
        ...deviceGroup,
        children: deviceGroup.children.filter(
          (item) => item.to !== "/inputDevice",
        ),
      },
      maintenanceItem,
      helpItem,
    ];

    const openGroups = reactive<Record<string, boolean>>({
      "device-management": true,
      "category-management": true,
    });

    const menuEntries = computed(() =>
      store.state.permission === "admin" ? adminMenuEntries : userMenuEntries,
    );
    const permissionLabel = computed(() =>
      store.state.permission === "admin" ? "Quản trị viên" : "Người dùng",
    );
    const initials = computed(() => {
      const source = props.fullName || "QL";
      return source
        .split(" ")
        .filter(Boolean)
        .slice(-2)
        .map((part: string) => part.charAt(0).toUpperCase())
        .join("");
    });
    const maintenanceAlertTitle = computed(() => {
      if (maintenanceAlert.error) {
        return "Không tải được cảnh báo bảo dưỡng. Vui lòng kiểm tra API hoặc tải lại trang.";
      }
      if (!maintenanceAlert.loaded) return "Đang tải cảnh báo bảo dưỡng...";
      if (!maintenanceAlert.tongSo) return "Không có lịch bảo dưỡng đến hạn";
      return `${maintenanceAlert.tongSo} lịch bảo dưỡng đến hạn: ${maintenanceAlert.quaHan} quá hạn, ${maintenanceAlert.sapDenHan} đến hạn trong ${maintenanceAlert.soNgayCanhBao} ngày tới`;
    });

    const loadMaintenanceAlert = async () => {
      try {
        const [{ data }, configResponse] = await Promise.all([
          QLTSRepository.getListBaoDuongDenHan(),
          QLTSRepository.getCauHinh(),
        ]);
        const dueRows = Array.isArray(data) ? data : [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        maintenanceAlert.tongSo = dueRows.length;
        maintenanceAlert.quaHan = dueRows.filter((item: any) => {
          const date = new Date(item.thoiGianBaoDuongTiepTheo);
          date.setHours(0, 0, 0, 0);
          return date < today;
        }).length;
        maintenanceAlert.sapDenHan = maintenanceAlert.tongSo - maintenanceAlert.quaHan;
        maintenanceAlert.soNgayCanhBao = Number(
          configResponse.data?.thoiGianBaoDuong ??
            configResponse.data?.ThoiGianBaoDuong ??
            0,
        );
        maintenanceAlert.error = false;
      } catch {
        maintenanceAlert.tongSo = 0;
        maintenanceAlert.error = true;
      } finally {
        maintenanceAlert.loaded = true;
      }
    };

    const syncOpenGroups = () => {
      menuEntries.value.forEach((entry) => {
        if (entry.type !== "group") return;
        if (entry.children.some((item) => item.to === route.path)) {
          openGroups[entry.key] = true;
        }
      });
    };

    const toggleGroup = (key: string) => {
      openGroups[key] = !openGroups[key];
    };

    const logout = () => {
      store.dispatch("clearToken");
      store.dispatch("clearPermission");
      store.dispatch("clearFullName");
      store.dispatch("clearTeam");
      store.dispatch("clearSessionExpired");
      router.push({ name: "login" });
    };

    watch(() => route.path, syncOpenGroups, { immediate: true });

    onMounted(() => {
      loadMaintenanceAlert();
      window.addEventListener("maintenance-alert-refresh", loadMaintenanceAlert);
      maintenanceAlertTimer = window.setInterval(loadMaintenanceAlert, 5 * 60 * 1000);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("maintenance-alert-refresh", loadMaintenanceAlert);
      if (maintenanceAlertTimer !== undefined) {
        window.clearInterval(maintenanceAlertTimer);
      }
    });

    return {
      menuEntries,
      initials,
      logout,
      maintenanceAlert,
      maintenanceAlertTitle,
      openGroups,
      PanelLeftClose,
      PanelLeftOpen,
      permissionLabel,
      toggleGroup,
    };
  },
};
</script>

<style scoped>
@media (max-height: 760px) {
  .sidebar-header {
    min-height: 2.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .sidebar-menu {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
  }

  .sidebar-link,
  .sidebar-child-link {
    min-height: 2rem;
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
  }

  .sidebar-group-title {
    min-height: 1.75rem;
  }

  .sidebar-footer {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
  }
}

@media (max-height: 620px) {
  .sidebar-menu > p {
    display: none;
  }
}
</style>
